import type { TableOptions } from '@tanstack/react-table';

/** Stable id for a row. */
export type GetRowId<TRow> = (row: TRow) => string;

/**
 * Pairs a table feature's config with the row identity the table resolves for
 * it, so no feature carries an identity of its own. `undefined` when the feature
 * is off.
 */
export function withRowId<TConfig extends object, TRow>(
  config: TConfig | undefined,
  getRowId: GetRowId<TRow> | undefined,
): (TConfig & { getRowId: GetRowId<TRow> }) | undefined {
  return config && getRowId ? { ...config, getRowId } : undefined;
}

/**
 * Builds the one `getRowId` table option that row selection, row expansion and
 * the active row all key their state by, so two enabled features can't disagree
 * on what identifies a row.
 *
 * Sub-rows keep TanStack's own `parent.index` composition, since the caller's id
 * function describes parent rows.
 */
export function toGetRowIdOption<TRow>(getRowId: GetRowId<TRow>): NonNullable<TableOptions<TRow>['getRowId']> {
  return (row, index, parent) => (parent ? `${parent.id}.${index}` : getRowId(row));
}
